import React, { useMemo, useState } from "react";
import { Button, Select, Spin, Tag } from "antd";
import { ReloadOutlined } from "@ant-design/icons";
import { useTimeBoundDeliveryStats } from "../../../hooks/useBST2Dashboard";
import "./style.scss";

const TIMEZONES = [
  { value: "Asia/Kolkata", label: "IST (Asia/Kolkata)" },
  { value: "Europe/Berlin", label: "CET (Europe/Berlin)" },
  { value: "UTC", label: "UTC" },
];

const METRICS = [
  { key: "openingPending", label: "Opening pending" },
  { key: "newReceived", label: "New received" },
  { key: "delivered", label: "Delivered" },
  { key: "closingPending", label: "Closing pending" },
];

const pad = (value) => String(value).padStart(2, "0");

const toDateTimeLocal = (date) =>
  `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(
    date.getHours()
  )}:${pad(date.getMinutes())}`;

const startOfToday = () => {
  const date = new Date();
  date.setHours(0, 0, 0, 0);
  return toDateTimeLocal(date);
};

const formatNumber = (value) => (value ?? 0).toLocaleString("en-IN");

const TimeBoundDeliveryStats = () => {
  const [from, setFrom] = useState(startOfToday);
  const [to, setTo] = useState(() => toDateTimeLocal(new Date()));
  const [timezone, setTimezone] = useState("Asia/Kolkata");

  const params = useMemo(() => ({ from, to, timezone }), [from, to, timezone]);
  const canQuery = Boolean(from && to && from < to);
  const { data, isLoading, isError, error, refetch, isFetching } = useTimeBoundDeliveryStats(params, {
    enabled: canQuery,
  });

  return (
    <section className="time_bound_delivery_stats">
      <div className="time_bound_delivery_stats_header">
        <div>
          <h2>TimeBoundDeliveryStats</h2>
          <p>Opening pending + New received − Delivered = Closing pending.</p>
        </div>
        <Button icon={<ReloadOutlined />} onClick={() => refetch()} loading={isFetching}>
          Refresh
        </Button>
      </div>

      <div className="time_bound_delivery_stats_controls">
        <label>
          <span>From</span>
          <input type="datetime-local" value={from} onChange={(e) => setFrom(e.target.value)} />
        </label>
        <label>
          <span>To</span>
          <input type="datetime-local" value={to} onChange={(e) => setTo(e.target.value)} />
        </label>
        <label>
          <span>Timezone</span>
          <Select value={timezone} onChange={setTimezone} options={TIMEZONES} style={{ minWidth: 220 }} />
        </label>
      </div>

      {!canQuery ? <p className="time_bound_delivery_stats_error">To must be after from.</p> : null}

      {isLoading ? (
        <Spin />
      ) : isError ? (
        <p className="time_bound_delivery_stats_error">
          {error?.response?.data?.message || error?.message || "Failed to load stats."}
        </p>
      ) : (
        <>
          <div className="time_bound_delivery_stats_meta">
            <span>
              {data?.from ? new Date(data.from).toLocaleString("en-IN") : "-"} →{" "}
              {data?.to ? new Date(data.to).toLocaleString("en-IN") : "-"} ({data?.timezone})
            </span>
            <Tag color={data?.reconciled ? "success" : "error"}>
              {data?.reconciled ? "Reconciled" : "Not reconciled"}
            </Tag>
          </div>
          <div className="time_bound_delivery_stats_cards">
            {METRICS.map(({ key, label }) => (
              <div className="time_bound_delivery_stats_card" key={key}>
                <div>{label}</div>
                <strong>{formatNumber(data?.[key])}</strong>
              </div>
            ))}
          </div>
        </>
      )}
    </section>
  );
};

export default TimeBoundDeliveryStats;
